/**
 * Practice on the till: staff learn on the real menu and tables, and nothing
 * they do counts (`/v1/practice`).
 *
 * A manager starts it from This tablet and ends it there. In between the
 * till looks and works as it always does, under a band nobody can miss, and
 * the server keeps everything apart: no takings, no receipt numbers, no
 * stock, no loyalty, nothing on a kitchen screen, no card machine, no
 * emails. Ending practice throws all of it away; so does the day running
 * out.
 *
 * With no connection the till does not practise (`offline/gate.tsx`): a sale
 * kept on the tablet is sent as a real one when the line is back, and a
 * practice sale must never be.
 */

import { useEffect, useState } from 'react';
import {
  displayLocale,
  useEndPractice,
  usePermission,
  usePractice,
  useStartPractice,
} from '@restaurant/api-client';
import { ActionError, Button, Sheet } from '@restaurant/ui';

const MANAGE = 'practice.session.manage';

/** "Mon 13:37": practice ends a day after it began, so the day is part of it. */
function untilWords(until: Date | null): string {
  if (!until) return '';
  return until.toLocaleString(displayLocale(), {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Across the top of every screen while the till practises, the lock screen
 * included. It takes its height out of the till's frame (`--practice-band`),
 * so nothing is hidden under it and the page still never scrolls.
 */
export function PracticeBand() {
  const { practising, until } = usePractice();

  useEffect(() => {
    const root = document.documentElement;
    if (practising) root.dataset.practice = 'on';
    else delete root.dataset.practice;
    return () => {
      delete root.dataset.practice;
    };
  }, [practising]);

  if (!practising) return null;
  return (
    <div className="prac-band" role="status">
      <strong className="prac-band__words">Practice — nothing here counts</strong>
      {until && <span className="prac-band__until">Ends by {untilWords(until)}</span>}
    </div>
  );
}

/** "Practice" in This tablet. Only managers start and end it. */
export function PracticeRow({ locationId }: { locationId: string | undefined }) {
  const { practising, until } = usePractice();
  const mayManage = usePermission(MANAGE);
  const [open, setOpen] = useState(false);

  if (!practising && !mayManage) return null;

  return (
    <>
      <ul className="os__more pos__shift-rows">
        <li>
          <button
            type="button"
            className="os__more-item"
            onClick={() => setOpen(true)}
            disabled={!mayManage}
          >
            <span className="os__more-label">Practice</span>
            <span className="os__more-hint">
              {practising
                ? mayManage
                  ? `On · ends by ${untilWords(until)}`
                  : 'On · a manager ends it'
                : 'Learn the till: nothing counts'}
            </span>
          </button>
        </li>
      </ul>
      {open &&
        (practising ? (
          <EndPracticeSheet onDone={() => setOpen(false)} />
        ) : (
          <StartPracticeSheet locationId={locationId} onDone={() => setOpen(false)} />
        ))}
    </>
  );
}

function StartPracticeSheet({
  locationId,
  onDone,
}: {
  locationId: string | undefined;
  onDone: () => void;
}) {
  const start = useStartPractice();

  return (
    <Sheet
      title="Practise on this till"
      onDismiss={onDone}
      actions={
        <Button
          variant="primary"
          size="lg"
          block
          disabled={!locationId}
          loading={start.isPending}
          onClick={() => {
            if (!locationId) return;
            start.mutate({ locationId }, { onSuccess: onDone });
          }}
        >
          Start practice
        </Button>
      }
    >
      <p className="prac__lead">
        The till works as it always does, on the real menu and tables, and nothing done on it
        counts.
      </p>
      <ul className="prac__facts">
        <li>No takings, receipt numbers or Z report. Receipts print as practice slips.</li>
        <li>No stock used, no loyalty points, nothing on the kitchen screens.</li>
        <li>Cards are not sent to the card machine. Gift cards and accounts are off.</li>
        <li>Everyone else sees the restaurant as it really is.</li>
      </ul>
      <p className="prac__note">
        Everything done in practice is thrown away when you end it, or after a day at the
        latest. Practice needs a connection. Starting and ending it are on the audit log.
      </p>
      <ActionError error={start.error} action="Starting practice" />
    </Sheet>
  );
}

function EndPracticeSheet({ onDone }: { onDone: () => void }) {
  const end = useEndPractice();

  return (
    <Sheet
      title="End practice"
      onDismiss={onDone}
      actions={
        <Button
          variant="primary"
          size="lg"
          block
          loading={end.isPending}
          onClick={() => end.mutate(undefined, { onSuccess: onDone })}
        >
          End practice
        </Button>
      }
    >
      <p className="prac__lead">
        Every practice order on this till is thrown away, and the till goes back to counting.
      </p>
      <ActionError error={end.error} action="Ending practice" />
    </Sheet>
  );
}

/** What the till shows offline while practising, instead of selling. */
export function PracticeNeedsConnection() {
  return (
    <div className="prac-offline" role="status">
      <h1 className="prac-offline__title">Practice needs a connection</h1>
      <p className="prac-offline__body">
        A sale made with no connection is kept on this tablet and recorded when the line is
        back, and a practice sale must never be. Practice carries on as soon as the connection
        does.
      </p>
    </div>
  );
}
